import { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  useTheme
} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MaterialTable from 'material-table';
import PatchedPagination from './PatchedPagination';
import { serverContext, appContext, putToServer, callServer } from '../App';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import { grey, green } from '@material-ui/core/colors';
import moment from 'moment';
import { useGetUsergroups } from '../customEffects';
import SelectUsergroups from './SelectUsergroups';
import {
  securityClassificationMap,
  securityClassificationLookup,
  userRoleMap,
  userRoleLookup
} from '../serverReducer';
import { tableIcons } from '../commonTypes';
import { useTableTheming } from './useTableTheming';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  user: {
    padding: theme.spacing(2),
    marginLeft: theme.spacing(4),
    color: 'grey',
  },
  select: {
    minWidth: 240,
    maxWidth: 600,
  },
  formControl: {
    margin: theme.spacing(1),
    marginLeft: 40,
    minWidth: 240,
    maxWidth: 600,
  },
  formControlLabel: {
    color: 'grey',
  },
  offline: {
    backgroundColor: grey[500],
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  online: {
    backgroundColor: green[500],
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
}));

// Helper to build query strings
const buildQuery = (params) =>
  Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

// Translate server users to table rows
const translateUsers = (users) => {
  if (!Array.isArray(users)) return [];
  try {
    return users.map((u) => ({
      id: u._id,
      userId: u._id,
      username: u.username,
      firstName: u.firstName,
      lastName: u.lastName,
      onlineCount: u.onlineCount,
      email: u.email,
      authorizationRole:
        u.authorizationRole === 'superAdmin'
          ? 'superAdmin'
          : userRoleMap[u.authorizationRole],
      securityClearance: securityClassificationMap[u.securityClearance],
      usergroups: u.usergroups,
      usergroupsAdmin: u.usergroupsAdmin,
      country: u.country,
      registered: u.registered,
      lastAccess: u.lastAccess,
    }));
  } catch (error) {
    console.error('Error translating users:', error);
    return [];
  }
};

// Avatar + badge for online status
const StatusAvatar = ({ username, onlineCount }) => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <Badge badgeContent={onlineCount} color="secondary" overlap="rectangular">
        <Avatar className={onlineCount > 0 ? classes.online : classes.offline}>
          {username.charAt(0).toUpperCase()}
        </Avatar>
      </Badge>
    </div>
  );
};
StatusAvatar.propTypes = {
  username: PropTypes.string.isRequired,
  onlineCount: PropTypes.number.isRequired,
};

const UsersTableTitle = () => (
  <div style={{ display: 'flex' }}>
    <Typography noWrap variant="overline" display="block">
      Users
    </Typography>
  </div>
);

const UsersTable = () => {
  const { server } = useContext(serverContext);
  const { dispatchApp } = useContext(appContext);
  const [selectedRow, setSelectedRow] = useState(null);
  const tableTheme = useTableTheming(selectedRow);
  const [data, setData] = useState([]);

  useGetUsergroups();

  useEffect(() => {
    if (server.users) setData(translateUsers(server.users));
  }, [server.users]);

  const themeObj = useTheme();
  const isMdUp = useMediaQuery(themeObj.breakpoints.up('md'));
  const classes = useStyles();

  // Stable render callbacks
  const renderStatus = useCallback((row) => <StatusAvatar {...row} />, []);
  const renderEmail = useCallback((row) => row.email || 'N/A', []);
  const renderGroups = useCallback(
    (row) =>
      row.usergroups ? (
        <Tooltip title={row.usergroups.map((ug) => ug.name).join(', ')}>
          <div>{row.usergroups.length}</div>
        </Tooltip>
      ) : (
        '0'
      ),
    []
  );
  const renderCountry = useCallback((row) => row.country || 'N/A', []);
  const renderRegistered = useCallback(
    (row) => moment(row.registered).format('MMM Do YY, HH:mm'),
    []
  );
  const renderLastAccess = useCallback(
    (row) => moment(row.lastAccess).format('MMM Do YY, HH:mm'),
    []
  );

  // Columns memoized
  const columns = useMemo(
    () => [
      {
        title: '',
        field: 'onlineCount',
        tooltip: 'Status',
        editable: 'never',
        width: '4%',
        render: renderStatus,
      },
      { title: 'Username', field: 'username', tooltip: 'Username', editable: 'never' },
      { title: 'First Name', field: 'firstName', tooltip: 'First name', hidden: !isMdUp },
      { title: 'Last Name', field: 'lastName', tooltip: 'Last name', hidden: !isMdUp },
      {
        title: 'Email',
        field: 'email',
        tooltip: `Sender's email`,
        width: '15%',
        hidden: !isMdUp,
        render: renderEmail,
      },
      { title: 'Role', field: 'authorizationRole', tooltip: "User's role", lookup: userRoleLookup },
      {
        title: 'Groups',
        field: 'usergroups',
        tooltip: 'User groups',
        editable: 'never',
        render: renderGroups,
      },
      { title: 'Country', field: 'country', tooltip: `User's country`, render: renderCountry },
      {
        title: 'Clearance',
        field: 'securityClearance',
        tooltip: `User's security classification`,
        width: '12%',
        lookup: securityClassificationLookup,
      },
      {
        title: 'Registered',
        field: 'registered',
        tooltip: 'User registration date',
        editable: 'never',
        hidden: !isMdUp,
        render: renderRegistered,
      },
      {
        title: 'Last access',
        field: 'lastAccess',
        tooltip: 'Last access',
        editable: 'never',
        hidden: !isMdUp,
        render: renderLastAccess,
      },
    ],
    [isMdUp, renderStatus, renderEmail, renderGroups, renderCountry, renderRegistered, renderLastAccess]
  );

  // Actions
  const actions = useMemo(
    () => [
      {
        icon: tableIcons.DeleteIcon,
        tooltip: 'Delete User(s)',
        onClick: async (_event, rows) => {
          const names = rows.map((u) => u.username).join(',');
          try {
            await callServer('delete', `users?users=${encodeURIComponent(names)}`, undefined, server);
          } catch (error) {
            dispatchApp({
              type: 'SHOW_ALERT',
              payload: {
                title: 'Error',
                message: `Error deleting user(s): ${
                  error.response ? error.response.data : error.message
                }`,
              },
            });
          }
        },
      },
    ],
    [dispatchApp, server]
  );

  // Handlers
  const handleUserGroupsChange = useCallback(
    async (userId, ugs) => {
      try {
        const params = { usergroups: ugs.map((g) => g._id).join(',') };
        await putToServer(`users/${userId}?${buildQuery(params)}`, {}, server);
      } catch (error) {
        dispatchApp({
          type: 'SHOW_ALERT',
          payload: {
            title: 'Error',
            message: `Error updating user: ${
              error.response ? error.response.data : error.message
            }`,
          },
        });
      }
    },
    [dispatchApp, server]
  );

  const handleUserGroupsAdminChange = useCallback(
    async (userId, ugs) => {
      try {
        const params = { usergroupsAdmin: ugs.map((g) => g._id).join(',') };
        await putToServer(`users/${userId}?${buildQuery(params)}`, {}, server);
      } catch (error) {
        dispatchApp({
          type: 'SHOW_ALERT',
          payload: {
            title: 'Error',
            message: `Error updating user: ${
              error.response ? error.response.data : error.message
            }`,
          },
        });
      }
    },
    [dispatchApp, server]
  );

  const handleRowUpdate = useCallback(
    (newData) =>
      new Promise(async (resolve, reject) => {
        try {
          const params = {
            firstName: newData.firstName,
            lastName: newData.lastName,
            company: newData.company,
            email: newData.email,
            country: newData.country,
            securityClearance: securityClassificationLookup[newData.securityClearance],
            authorizationRole: userRoleLookup[newData.authorizationRole],
          };
          const res = await putToServer(
            `users/${newData.userId}?${buildQuery(params)}`,
            {},
            server
          );
          dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: res.data } });
          resolve();
        } catch (error) {
          dispatchApp({
            type: 'SHOW_ALERT',
            payload: {
              title: 'Error',
              message: `Error updating user: ${
                error.response ? error.response.data : error.message
              }`,
            },
          });
          reject();
        }
      }),
    [dispatchApp, server]
  );

  return (
    <MaterialTable
      className={classes.root}
      icons={tableIcons}
      columns={columns}
      data={data}
      title={<UsersTableTitle />}
      detailPanel={(rowData) => {
        const uGroups = Array.isArray(rowData.usergroups)
          ? rowData.usergroups
          : [];
        const uGroupsAdmin = Array.isArray(rowData.usergroupsAdmin)
          ? rowData.usergroupsAdmin
          : [];

        return (
          <div>
            <FormControl className={classes.formControl}>
              <Tooltip title="Assign allowed usergroups">
                <FormControlLabel
                  label={<Typography className={classes.formControlLabel}>Usergroups:</Typography>}
                  labelPlacement="start"
                  control={
                    rowData.authorizationRole !== userRoleMap['groupAdmin'] &&
                    rowData.authorizationRole !== 'superAdmin' ? (
                      <SelectUsergroups
                        selectedUserGroups={uGroups}
                        onUserGroupsChange={(ugs) => handleUserGroupsChange(rowData.userId, ugs)}
                      />
                    ) : (
                      <Typography>All</Typography>
                    )
                  }
                />
              </Tooltip>
            </FormControl>
            {rowData.authorizationRole === userRoleMap['groupAdmin'] && (
              <FormControl className={classes.formControl}>
                <Tooltip title="Assign usergroups that user can control">
                  <FormControlLabel
                    label={<Typography className={classes.formControlLabel}>Group Admin:</Typography>}
                    labelPlacement="start"
                    control={
                      <SelectUsergroups
                        canDeleteDemo
                        selectedUserGroups={uGroupsAdmin}
                        onUserGroupsChange={(ugs) => handleUserGroupsAdminChange(rowData.userId, ugs)}
                      />
                    }
                  />
                </Tooltip>
              </FormControl>
            )}
          </div>
        );
      }}
      onRowClick={(evt, row) => setSelectedRow(row)}
      options={{
        selection: true,
        cellStyle: tableTheme.cellStyle,
        headerStyle: tableTheme.headerStyle,
        filterRowStyle: tableTheme.filterRowStyle,
        rowStyle: tableTheme.rowStyle,
      }}
      actions={actions}
      editable={{
        isEditable: (rowData) =>
          rowData.username !== 'guest' && rowData.username !== 'superAdmin',
        isDeletable: (rowData) => rowData.username !== 'guest',
        onRowUpdate: handleRowUpdate,
      }}
      components={{
        Pagination: PatchedPagination,
      }}
    />
  );
};

export default UsersTable;
